/*
 *   Copyright (c) 2020 BuserNet Consulting LLC
 *   All rights reserved.
 */
import _forEach from 'lodash/forEach';
import bnd3Service from '../../../services/bnd3.service';
import utilityService from '../../../services/utility.service';
import ChartConst from '../../../constants/chart.constant';
import AppConst from '../../../constants/app.constant';
import DefConst from '../../../constants/def.constant';
import bnChartDataProcessService from '../../../services/chartDataProcess.service';
import bnChartDataService from '../../../services/chartData.service';
import d3 from '../../../globalexports/d3.export';
import { validateAloftData } from '../../../services/validation/validateAloftData';

// $rootScope
function routeCeilingChartService() {
  const content = {};
  content.drawChart = function (
    sourceData,
    chartData,
    chartObj,
    chartHeight,
    width,
    x,
    y1,
    y2,
    topSpace,
    ceilingMaxValueAllowed,
    linearScaleTicks,
    yAxisRectWidth,
    yAxisRightRectWidth,
    aloftData,
    settings,
    fullData,
  ) {
    let visBars;
    let visStrip;
    let visTemp;
    const rangeData = [];
    let cloudIndex;
    let heightOfCloud;
    let aloftTmpContainer;
    let aloftGridContainer;
    let gridColumns;
    const cloudsHeightObject = [];
    let airGairSigmetContainer;
    let airSigmetContainer;
    let airmetTrends;

    let tmpDataLowerBoundPath;
    const aloftCloudIndexMap = DefConst[DefConst.aloftCloudIndexMapKey + settings.dataMode];
    if (!sourceData) {
      return null;
    }
    if (validateAloftData(aloftData)) {
      aloftData = bnChartDataService.processRouteAloftData(aloftData, settings);
      if (chartData && chartData[0] && chartData[0].key === ChartConst.ChartLabelSpecs.cig.key) {
        let k = 0;
        while (k < sourceData.length) {
          if (chartData[0].values[k] && chartData[0].values[k].cig_real && aloftData[k]
            && aloftData[k].altitudes) {
            bnChartDataService.refineCeilingValueFromAloft(chartData[0].values[k], aloftData[k]
              .altitudes, sourceData[k], settings);
          }
          k += 1;
        }
      }
    }
    chartObj.append('g')
      .attr('class', 'ceiling-background')
      .append('rect')
      .attr('x', yAxisRectWidth)
      .attr('width', width - (yAxisRectWidth + yAxisRightRectWidth))
      .attr('height', chartHeight)
      .attr('fill', ChartConst.ceilingAreaBackgroundColor);
    const elvX = d3.scaleLinear()
      .range([yAxisRectWidth, width - yAxisRightRectWidth])
      .unknown(yAxisRectWidth);
    elvX.domain([0, AppConst.elevationSamples]);
    // Y-Axis left side ChartConst
    const yAxisLeft = d3.axisRight()
      .scale(y1)
      .tickValues(linearScaleTicks)
      .ticks(ChartConst.GraphConfig.yAxis.ticks)
      .tickFormat((d) => utilityService.abbreviateCeiling(d, ChartConst.ChartLabelSpecs.cig
        .tooltip.mobileUnit))
      .tickSizeInner(-width) // To show horizontal gridlines
      .tickSizeOuter(0)
      .tickPadding(ChartConst.GraphConfig.yAxis.tickPadding);
    // yAxisLeftMinor is for drawing ticks line inside chart
    const yAxisLeftMinor = d3.axisLeft()
      .scale(y1)
      .tickValues(linearScaleTicks)
      .ticks(ChartConst.GraphConfig.yAxis.ticks)
      .tickFormat('')
      .tickSizeInner(-width) // To show horizontal gridlines
      .tickSizeOuter(0)
      .tickPadding(ChartConst.GraphConfig.yAxis.tickPadding);
    const aloftTempLine = d3.line().curve(d3.curveLinear).defined(
      (d) => d.y !== null,
    ).x((ele, key) => x(sourceData[key]
      ? sourceData[key].arrival_time
      : null))
      .y((d) => {
        let yValue = d.y;
        if (yValue === ceilingMaxValueAllowed) {
          yValue = ceilingMaxValueAllowed + AppConst.extendedCeiling.min;
        } else if (yValue === AppConst.extendedCeiling.min) {
          yValue = AppConst.extendedCeiling.min - AppConst.extendedCeiling.min;
        }
        return y1(yValue);
      });
    // Setting Domain for X-axis
    x.domain(d3.extent(sourceData, (d) => d.arrival_time));
    // console.log(ceilingMaxValueAllowed + ChartConst.ceilingDomain.maxOffset);
    y1.domain([ChartConst.ceilingDomain.min, ceilingMaxValueAllowed + ChartConst.ceilingDomain
      .maxOffset,
    ]);
    y2.domain([ChartConst.visDomain.min - ChartConst.visDomain.minOffset, ChartConst.visDomain
      .max + ChartConst.visDomain.maxOffset,
    ]);
    chartObj.append('rect')
      .attr('class', 'clear-rect')
      .attr('x', yAxisRectWidth)
      .attr('y', (chartHeight))
      .attr('width', width + 2)
      .attr('height', topSpace / 2)
      .style('fill', '#DDDDDD')
      .style('stroke', 'none');

    // Drawing Y-axis left
    chartObj.append('g')
      .attr('class', 'y axis y-left y-left-negative-lines')
      .call(yAxisLeft);
    chartObj.append('g')
      .attr('class', 'y axis y-left y-left-tick-highlighter')
      .call(yAxisLeftMinor);

    chartObj.append('clipPath').attr('id', 'cliproute').append('rect').attr('x', yAxisRectWidth)
      .attr('width', width - (yAxisRectWidth + yAxisRightRectWidth))
      .attr('height', chartHeight - ChartConst.ceilingClipPathReduction);
    // Drawing VISIBILITY bars
    // Set this value if want to show rect beyond boundary
    // leakVisAtBoundaryByValue = 0;
    /* Ceiling using null for 12000ft */
    if (chartData && chartData[0]) {
      rangeData.push(bnChartDataProcessService.processCeilingRangeData(utilityService.clone([
        chartData[0],
      ]))[0]);
    }
    cloudIndex = 0;
    if (validateAloftData(aloftData)) {
      while (cloudIndex < aloftCloudIndexMap.length) {
        heightOfCloud = y1(aloftCloudIndexMap[cloudIndex]) - y1(Math.min(
          ceilingMaxValueAllowed,
          aloftCloudIndexMap[cloudIndex + 1],
        ));
        cloudsHeightObject.push(heightOfCloud);
        cloudIndex += 1;
      }
      aloftGridContainer = chartObj.append('g')
        .attr('id', 'aloftGridContainerRoute')
        .attr('class', 'aloftGridContainerRoute');
      gridColumns = aloftGridContainer.selectAll('col').data(aloftData).enter().append('g')
        .attr('class', (...args) => {
          const i = args[1];
          return `col col-${i + 1}`;
        })
        .attr('data', (d) => d.model_ts)
        .attr('key-value', (...args) => {
          const key = args[1];
          return key;
        })
        .attr('transform', (d, key) => {
          const positionObj = bnd3Service.getNextCurrentPrevX(
            aloftData,
            x,
            d,
            'arrival_time',
            key,
          );
          return `translate(${positionObj.currentX - ((positionObj.currentX - positionObj.prevX) / 2)},  0)`;
        });
      gridColumns.selectAll('rect .row').data((d) => d.altitudes.filter((p, key) => {
        // Filter out values where CIG is above max allowed CIG
        p.altitudeIdx = key;
        p.valueOfAlt = aloftCloudIndexMap[key];
        if (aloftCloudIndexMap[key] >= ChartConst.cloudThreshold.seperateThresholdAlt && p
          .rh < ChartConst.cloudThreshold.level1.fl320threshold) {
          p.markedTransparent = true;
        } else if (!p.rh || p.rh < ChartConst.cloudThreshold.level1.threshold) {
          p.markedTransparent = true;
        }
        return ((!p.markedTransparent) && aloftCloudIndexMap[key]
            <= ceilingMaxValueAllowed);
      }))
        .enter()
        .append('rect')
        .style('fill', (d) => {
          let fillColor = '';
          if (d.valueOfAlt >= ChartConst.cloudThreshold.seperateThresholdAlt) {
            if (d.rh > ChartConst.cloudThreshold.level1.fl320threshold && d.rh < ChartConst
              .cloudThreshold.level2.fl320threshold) {
              fillColor = ChartConst.cloudThreshold.level1.color;
            } else {
              fillColor = ChartConst.cloudThreshold.level2.color;
            }
          } else if (d.rh > ChartConst.cloudThreshold.level1.threshold && d.rh < ChartConst
            .cloudThreshold.level2.threshold) {
            fillColor = ChartConst.cloudThreshold.level1.color;
          } else {
            fillColor = ChartConst.cloudThreshold.level2.color;
          }
          d.fillColor = fillColor;
          return d.fillColor;
        })
        .style('stroke-width', '0.5px')
        .style('stroke', (d) => d.fillColor)
        .style('shape-rendering', 'crispedges')
        .attr('class', 'aloft-clouds')
        .attr('rhvalue', (d) => `${d.rh}:${d.valueOfAlt}:${d.hgt}`)
        .attr('height', (d) => {
          const key = d.altitudeIdx;
          if (cloudsHeightObject[key]) {
            return cloudsHeightObject[key];
          }
          return 0;
        })
        .attr('width', (...args) => {
          const d = args[0];
          const selfEle = utilityService.getSelfElement(args);
          const keyValue = parseInt(d3.select(selfEle.parentNode).attr('key-value'), 10);
          // attaching arrivalTime to d because bnd3Service.getNextCurrentPrevX expect
          // to d contain right prop to calc currentX
          d.arrival_time = aloftData[keyValue].arrival_time;
          const positionObj = bnd3Service.getNextCurrentPrevX(
            aloftData,
            x,
            d,
            'arrival_time',
            keyValue,
          );
          return ((positionObj.currentX - positionObj.prevX) / 2) + ((positionObj.nextX
            - positionObj.currentX) / 2);
        })
        .attr('transform', (d) => {
          const key = d.altitudeIdx;
          if (cloudsHeightObject[key]) {
            return `translate(0, ${y1(aloftCloudIndexMap[key]) - cloudsHeightObject[key]})`;
          }
          return 'translate(0, 0)';
        });
    }
    // console.log(fullData);
    function drawAirmetComponents() {
      const nmXAxis = d3.scaleLinear()
        .range([yAxisRectWidth, width - yAxisRightRectWidth])
        .unknown(yAxisRectWidth);
      nmXAxis.domain([0, fullData.totalDistanceToTravel]);
      // Draw airmet/gairmet only in case of 1-Day view
      airGairSigmetContainer = chartObj.append('g')
        .attr('id', 'route-air-gair-sigmet-container')
        .attr('clip-path', 'url(#cliproute)');
      airSigmetContainer = airGairSigmetContainer.append('g')
        .attr('id', 'route-airsigmet-container');
      airGairSigmetContainer.append('g')
        .attr('id', 'route-gairsigmet-container');
      let airmetDataCollection = [];
      if (fullData && fullData.gairmet) {
        airmetDataCollection = bnd3Service.calcAirmetPositioningParametersRoute(
          fullData.gairmet,
          'valid_time_local',
          'expire_time_local',
          'altitude_min',
          'altitude_max',
          x,
          nmXAxis,
          y1,
          0,
          'gairmet',
          airmetDataCollection,
          fullData.waitForAloft,
          aloftData,
          settings,
          fullData,
          elvX,
          tmpDataLowerBoundPath,
        );
      }
      if (fullData && fullData.airsigmet) {
        airmetDataCollection = bnd3Service.calcAirmetPositioningParametersRoute(
          fullData
            .airsigmet,
          'valid_time_from_local',
          'valid_time_to_local',
          'altitude_min',
          'altitude_max',
          x,
          nmXAxis,
          y1,
          0,
          'airsigmet',
          airmetDataCollection,
          fullData
            .waitForAloft,
          aloftData,
          settings,
          fullData,
          elvX,
          tmpDataLowerBoundPath,
        );
      }
      bnd3Service.filterContainedRect(airmetDataCollection);
      bnd3Service.drawAirmetAndGairmet(airSigmetContainer, airmetDataCollection);
      const airmetTrendsTemp = bnChartDataProcessService.getAirmetTrendlineObject(
        airmetDataCollection,
        chartData[0],
        nmXAxis,

        x,
      );
      airmetTrends = airmetTrendsTemp;
      // var i = 0;
      // var j;
      // while (i < airmetTrendsTemp.length) {
      //     j = 0;
      //     while (j < suppressionObj.mets.length) {
      //         if (!airmetTrendsTemp[i].isSigmet && suppressionObj.isGairmetChecked &&
      //  airmetTrendsTemp[i].hazard_type_identifier === suppressionObj.mets[j].name
      //   && suppressionObj.mets[j].checked) {
      //             airmetTrends.push(airmetTrendsTemp[i]);
      //             break;
      //         }
      //         if (airmetTrendsTemp[i].isSigmet && suppressionObj.isSigmetChecked) {
      //             airmetTrends.push(airmetTrendsTemp[i]);
      //             break;
      //         }
      //         j += 1;
      //     }
      //     i += 1;
      // }
    }
    if (settings.enableCloudBelowCig === false && rangeData.length) {
      // now add titles to the axes
      chartObj.append('g')
        .attr('id', 'ceiling-bars-route')
        .selectAll('.series')
        .data(sourceData)
        .enter()
        .append('rect')
        .attr('clip-path', 'url(#cliproute)')
        .attr('class', 'ceiling-area-bars crispedges')
        .style('display', (d) => {
          if (d.cig_mapped >= ChartConst.lastCigValue) {
            return 'none';
          }
          return 'inherit';
        })
        .attr('x', (d, key) => {
          const positionObj = bnd3Service.getNextCurrentPrevX(
            sourceData,
            x,
            d,
            'arrival_time',
            key,
          );
          return (positionObj.currentX - ((positionObj.currentX - positionObj.prevX) / 2));
        })
        .style('display', (d) => {
          if (d.cig_mapped === null || d.cig_mapped === undefined || d.cig_mapped === 12000) {
            return 'none';
          }
          return 'inherit';
        })
        .attr('y', (d) => {
          d.yTransformValue = y1(d.cig_mapped || 0) - (ChartConst.cigCloudBlockHeight + 1);
          return d.yTransformValue;
        })
        .attr('width', (d, key) => {
          if (d.cig_mapped === null) {
            // if cig is null then dont draw it i.e. width 0
            return 0;
          }
          const positionObj = bnd3Service.getNextCurrentPrevX(
            sourceData,
            x,
            d,
            'arrival_time',
            key,
          );
          return ((positionObj.currentX - positionObj.prevX) / 2) + ((positionObj.nextX
            - positionObj.currentX) / 2);
        })
        .attr('height', (d) => chartHeight - d.yTransformValue);
    }

    // Drawing Y-axis left tick lines
    chartObj.append('g')
      .attr('class', 'y axis y-left y-grid-line-container')
      .call(yAxisLeftMinor);
    chartObj.append('g')
      .attr('id', 'cloud-cig')
      .selectAll('scatter-dots')
      .data(sourceData)
      .enter()
      .append('rect')
      .attr('clip-path', 'url(#cliproute)')
      .attr('class', 'crispedges')
      .attr('stroke', '#fff')
      .attr('fill', '#fff')
      .attr('x', (d, key) => {
        const positionObj = bnd3Service.getNextCurrentPrevX(
          sourceData,
          x,
          d,
          'arrival_time',
          key,
        );
        return (positionObj.currentX - ((positionObj.currentX - positionObj.prevX) / 2));
      })
      .style('display', (d) => {
        if (d.cig_mapped === null || d.cig_mapped === undefined || d.cig_mapped >= ChartConst
          .lastCigValue || (d.cig_mapped + d.airportDetails.el) > settings
          .maximumAltitudeAllowed) {
          return 'none';
        }
        return 'inherit';
      })
      .attr('y', (d) => y1((d.cig_mapped || 0) + d.airportDetails.el) - (ChartConst
        .cigCloudBlockHeight + 1))
      .attr('width', (d, key) => {
        if (d.cig_mapped === null || d.cig_mapped === undefined) {
          // if cig is null then dont draw it i.e. width 0
          return 0;
        }
        const positionObj = bnd3Service.getNextCurrentPrevX(
          sourceData,
          x,
          d,
          'arrival_time',
          key,
        );
        return ((positionObj.currentX - positionObj.prevX) / 2) + ((positionObj.nextX
          - positionObj.currentX) / 2);
      })
      .attr('height', ChartConst.cigCloudBlockHeight);

    function drawFreezingLines() {
      if (validateAloftData(aloftData)) {
        aloftTmpContainer = chartObj.append('g').attr('class', 'aloftTmpContainerRoute');
        tmpDataLowerBoundPath = aloftTmpContainer.append('path')
          .datum(aloftData.aloftTmpDataLowerBound)
          .style('stroke-dasharray', ('3, 3'))
          .attr('clip-path', 'url(#cliproute)')
          .attr('fill', 'none')
          .attr('stroke', ChartConst.ChartLabelSpecs.cigTmp.color)
          .attr('stroke-width', ChartConst.ChartLabelSpecs.cigTmp.strokeWidth)
          .attr('d', aloftTempLine);
        aloftData.aloftTmpDataUpperBound = bnChartDataProcessService.normalizeSecondaryRecord(
          aloftData.aloftTmpDataUpperBound,
          aloftData.aloftTmpDataLowerBound,
        );
        // Checking if all values of lower/upper freeze value are not at max ceiling
        if (bnChartDataProcessService.checkIfAllValueNotAtCeiling(aloftData
          .aloftTmpDataUpperBound, aloftData.aloftTmpDataLowerBound, settings)) {
          aloftTmpContainer.append('path')
            .datum(aloftData.aloftTmpDataUpperBound)
            .style('stroke-dasharray', ('3, 3'))
            .attr('clip-path', 'url(#cliproute)')
            .attr('fill', 'none')
            .attr('stroke', ChartConst.ChartLabelSpecs.cigTmp.color)
            .attr('stroke-width', ChartConst.ChartLabelSpecs.cigTmp.strokeWidth)
            .attr('d', aloftTempLine);
          const cigTmpSeries = utilityService.clone(ChartConst.ChartLabelSpecs.cigTmp);
          cigTmpSeries.values = aloftData.aloftTmpDataLowerBound;
          const cigTmpSeriesSecondary = utilityService.clone(ChartConst.ChartLabelSpecs.cigTmp);
          cigTmpSeriesSecondary.secondary = true;
          cigTmpSeriesSecondary.values = aloftData.aloftTmpDataUpperBound;
          chartData.push(cigTmpSeries);
          chartData.push(cigTmpSeriesSecondary);
        }
      }
    }
    // function drawElevation() {
    //     if (fullData.elevationData) {
    //         // console.log(fullData.elevationData);
    //         var areaElv = d3.area()
    //             .x(function () {
    //                 var key = arguments[1];
    //                 return elvX(key);
    //             })
    //             .y0(chartHeight)
    //             .y1(function (d) {
    //                 return y1(Math.max(Math.min(d.elevation, ceilingMaxValueAllowed), 0));
    //             });
    //         elevationContainer = trendSvg.append("g")
    //             .attr("id", "route-elevation-area")
    //             .attr("transform", "translate(0, " + (xAxisTopContainerHeight +
    //  flightWeatherStripHeight + rulesetStripHeight) + ")");
    //         elevationContainer.append("path")
    //             .data([fullData.elevationData])
    //             .attr("class", "area")
    //             .attr("fill", ChartConst.ChartLabelSpecs.elv.color).attr("d", areaElv);
    //     }
    // }
    function getValidChunkElvForVis(leftEdge, rightEdge) {
      let i = 0;
      const validChunk = [];
      let elvSampleY;
      const returnObj = {};
      if (fullData && fullData.elevationData) {
        while (i < fullData.elevationData.length) {
          elvSampleY = elvX(i);
          if (elvSampleY >= leftEdge && elvSampleY <= rightEdge) {
            validChunk.push(fullData.elevationData[i]);
          }
          i += 1;
        }
        i = 0;
        while (i < validChunk.length) {
          if (i === 0) {
            returnObj.minElvInSpread = validChunk[i].elevation;
            returnObj.maxElvInSpread = validChunk[i].elevation;
          }
          if (validChunk[i].elevation < returnObj.minElvInSpread) {
            returnObj.minElvInSpread = validChunk[i].elevation;
          }
          if (validChunk[i].elevation > returnObj.maxElvInSpread) {
            returnObj.maxElvInSpread = validChunk[i].elevation;
          }
          i += 1;
        }
      }
      return returnObj;
    }

    function drawVisibilitybarsRoute() {
      // ###visibility bars drawing
      // console.log(fullData.elevationData);
      // VISIBILITY bar container
      visBars = chartObj.append('g').attr('id', 'vis-bars-container-route');
      visStrip = visBars.selectAll('#vis-bars').data(sourceData).enter();
      visTemp = visStrip.append('g').attr('id', (d) => `vis-temp-${d.icao_id}`).attr(
        'transform',
        (d, key) => {
          const positionObj = bnd3Service.getNextCurrentPrevX(
            sourceData,
            x,
            d,
            'arrival_time',
            key,
          );
          const xValue = (positionObj.currentX - ((positionObj.currentX - positionObj.prevX)
            / 2));
          d.visRectWidth = ((positionObj.currentX - positionObj.prevX) / 2) + ((positionObj
            .nextX - positionObj.currentX) / 2);
          const returnObj = getValidChunkElvForVis(xValue, xValue + d.visRectWidth);
          d.visRectElvOffset = 0;
          // If elevation data is not available then no need of elevation offset
          if (returnObj.minElvInSpread) {
            d.visRectElvOffset = (chartHeight - y1(returnObj.minElvInSpread));
          }
          const yValue = Math.max(y1(ChartConst.visibilityTillWhatAltitude) - d
            .visRectElvOffset, 0);
          d.positionObj = positionObj;
          return `translate(${xValue},  ${yValue})`;
        },
      );
      visTemp.append('rect')
        .attr('fill', (d) => ChartConst.shadesForVisibility[d.vis || '7']).attr('width', (d) => d
          .visRectWidth + 0.1).attr('height', () => chartHeight - y1(ChartConst
          .visibilityTillWhatAltitude));
    }
    if (fullData && fullData.elevationData) {
      drawVisibilitybarsRoute();
    }
    drawFreezingLines();
    if (!fullData.waitForAloft) {
      drawAirmetComponents();
    } else if (fullData.waitForAloft && aloftData) {
      drawAirmetComponents();
    }
    // drawElevation();
    if (airmetTrends) {
      _forEach(airmetTrends, (item) => {
        chartData.push(item);
      });
    }
    return chartData;
  };
  return content;
}
export default routeCeilingChartService();
